import type { Selectbox_selectBox$data } from '@generated/Selectbox_selectBox.graphql';
import { ChevronDownIcon } from 'lucide-react';
import type * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFragment } from 'react-relay';
import type { ActionMeta, DropdownIndicatorProps, SingleValue } from 'react-select';
import { components } from 'react-select';
import { selectboxFragment } from './Selectbox.relay';
import { Select, reactSelectStyles, selectboxWrapperClassName } from './lib/SelectboxStyles';
import type { ISelectBoxItem, ISelectBoxProps } from './types';

type SelectValue = {
  value: string;
  label: string;
};

type SelectBoxRawItem = NonNullable<NonNullable<Selectbox_selectBox$data['options']>[number]>;

const isValidSelectItem = (option: SelectBoxRawItem | null | undefined): option is SelectBoxRawItem & { key: string; label: string } => {
  return Boolean(option && typeof option.key === 'string' && typeof option.label === 'string');
};

const convertOptions = (options: Selectbox_selectBox$data['options'], sort?: (a: ISelectBoxItem, b: ISelectBoxItem) => number, filter?: (a: ISelectBoxItem) => boolean) => {
  const safeOptions = options ?? [];
  const normalizedOptions: ISelectBoxItem[] = safeOptions.filter(isValidSelectItem).map((option) => ({
    key: option.key,
    label: option.label,
    data: option.data,
  }));
  const mutableOptions = [...normalizedOptions];
  const filteredOptions = typeof filter === 'function' ? mutableOptions.filter(filter) : mutableOptions;

  const filteredAndSortedOptions = typeof sort === 'function' ? filteredOptions.sort(sort) : filteredOptions;

  const itemsByKey: { [key: string]: ISelectBoxItem } = {};
  const convertedOptions = filteredAndSortedOptions.map((option) => {
    itemsByKey[option.key] = option;
    return {
      value: option.key,
      label: option.label,
    };
  });
  return {
    convertedOptions,
    itemsByKey,
  };
};

const DropdownIndicator = (props: DropdownIndicatorProps<SelectValue, false>) => (
  <components.DropdownIndicator {...props}>
    <ChevronDownIcon className="w-4 h-4 text-gray-400" />
  </components.DropdownIndicator>
);

const selectComponents = {
  DropdownIndicator,
  IndicatorSeparator: null,
};

const SelectBox: React.FC<ISelectBoxProps> = ({ selectBox: selectBoxKey, selected, className, width, onChange: onChangeProp, sort, filter }) => {
  const selectBox = useFragment(selectboxFragment, selectBoxKey);

  const [hasFocus, setHasFocus] = useState(false);
  const [value, setValue] = useState(selected);

  const { convertedOptions, itemsByKey } = useMemo(() => convertOptions(selectBox.options, sort, filter), [selectBox.options, sort, filter]);

  useEffect(() => {
    if (selected !== undefined) {
      setValue(selected);
    }
  }, [selected]);

  const setFocus = useCallback(() => {
    setHasFocus(true);
  }, []);

  const setBlur = useCallback(() => {
    setHasFocus(false);
  }, []);

  const handleChange = useCallback(
    (v: SingleValue<SelectValue>, _actionMeta: ActionMeta<SelectValue>) => {
      if (!onChangeProp || !v) {
        return;
      }

      const item = itemsByKey[v.value];
      if (!item) {
        return;
      }

      if (selected === undefined) {
        setValue(v.value);
      }

      onChangeProp(item.key, item);
    },
    [onChangeProp, itemsByKey, selected],
  );

  const classNames = ['SelectBox2', selectboxWrapperClassName];
  className && classNames.push(className);
  convertedOptions.length <= 1 && classNames.push('SelectBox2-singleOption');
  hasFocus && classNames.push('has-focus');

  const mergedStyles = useMemo(() => {
    if (!width) {
      return reactSelectStyles;
    }
    return {
      ...reactSelectStyles,
      container: (base: any) => ({ ...base, width }),
    };
  }, [width]);

  return (
    <Select
      className={classNames.join(' ')}
      classNamePrefix="rs"
      styles={mergedStyles}
      value={convertedOptions.find((opt) => opt.value === value) || null}
      options={convertedOptions}
      onChange={handleChange}
      isSearchable={false}
      isClearable={false}
      onFocus={setFocus}
      onBlur={setBlur}
      isDisabled={convertedOptions.length <= 1}
      components={selectComponents}
      menuPortalTarget={document.body}
    />
  );
};

export { SelectBox };
